import { type UseQueryOptions, useQueries } from '@tanstack/react-query';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@tempo-io/sui-accordion';
import { SystemStatusBadge } from '@tempo-io/sui-badge';
import { Button } from '@tempo-io/sui-button';
import { cn } from '@tempo-io/sui-class-utils';
import { Add, Close, Delete, Dependency } from '@tempo-io/sui-icons';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@tempo-io/sui-tablelayout';
import type React from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ItemPicker } from '@/components/common/custom-field-value/ItemPicker';
import { INSIGHTS_STALE_TIME } from '@/components/loop-dashboard/data/constants';
import { ItemTypeIcon } from '@/components/ui/item-type/itemTypeIcon';
import { ItemStatusBadge } from '@/components/ui/itemStatusBadge';
import { HealthApi } from '@/lib/api/dashboard/health';
import { extractServerMessage } from '@/lib/api/extractServerMessage';
import type { ItemHealth } from '@/lib/api/types/dashboard/health';
import type { ItemFiltersType } from '@/lib/api/types/itemFilters';
import type { ItemStatus } from '@/lib/api/types/itemStatus';
import type { Item } from '@/lib/api/types/items';
import type { ItemType, ItemTypeMap } from '@/lib/api/types/itemType';
import { useItemPanelNavigation } from '@/lib/hooks/item-panel/useItemPanelParams';
import {
  InvalidDependencyPairError,
  ReadOnlyAdapterError,
  SelfDependencyError,
} from '@/lib/mutations/item-store/dependencyErrors';
import { useItemDependenciesPanelQuery } from '@/lib/queries/common/useItemDependenciesPanelQuery';
import { useGetSchedulesByItemIds } from '@/lib/queries/work-integrations/useGetSchedules';
import { insightsQueryKeys } from '@/lib/query-keys/insights';
import { TabValue } from '@/lib/types/itemPanel';
import { associateById } from '@/lib/utils/common/associateById';
import { formatLocalDate } from '@/lib/utils/common/dateFormats';
import type { DependencyTabAdapter } from '../adapters/types';
import { useTabAdapter } from '../context/TabAdapterContext';

type ItemWithSchedule = Item & {
  expectedStartDateTime: string | null;
  expectedFinishDateTime: string | null;
};

type Column = {
  id: string;
  header: React.ReactNode;
  cell: (row: ItemWithSchedule, idx: number) => React.ReactNode;
};

type DependencySectionValue = 'predecessors' | 'successors';

type AddFlow =
  | { kind: 'idle' }
  | { kind: 'open'; section: DependencySectionValue }
  | { kind: 'submitting'; section: DependencySectionValue }
  | { kind: 'error'; section: DependencySectionValue; message: string };

type RemoveFlow =
  | { kind: 'idle' }
  | { kind: 'submitting'; section: DependencySectionValue; itemId: number }
  | { kind: 'error'; section: DependencySectionValue; itemId: number; message: string };

interface ScrollableTableProps {
  columns: Column[];
  rows: ItemWithSchedule[];
  rowClassName?: (row: ItemWithSchedule) => string | undefined;
}

const ScrollableTable: React.FC<ScrollableTableProps> = ({ columns, rows, rowClassName }) => {
  const { t } = useTranslation();
  return (
    <div className="max-h-[15rem] overflow-y-auto">
      <Table>
        <TableHeader>
          <TableRow>
            {columns.map((col) => (
              <TableHead key={col.id} className="h-10 px-2 font-medium text-text-neutral-primary">
                {col.header}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                className="text-center text-text-neutral-secondary"
              >
                {t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.noDependencies)}
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row, idx) => (
              <TableRow
                key={row.id}
                className={cn('border-b border-border-neutral last:!border-b', rowClassName?.(row))}
              >
                {columns.map((col) => (
                  <TableCell key={col.id} className="p-2">
                    {col.cell(row, idx)}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
};

const StatusCell = ({ row, itemStatuses }: { row: Item; itemStatuses: ItemStatus[] }) => {
  const itemStatus = itemStatuses.find((status) => status.id === row.itemStatusId);
  if (!itemStatus) {
    return null;
  }
  return <ItemStatusBadge color={itemStatus.color} label={itemStatus.name} />;
};

const DependencyNameCell = ({ row, itemTypes }: { row: Item; itemTypes: ItemType[] }) => {
  const { openItemPanel } = useItemPanelNavigation();
  const itemType = itemTypes.find((type) => type.id === row.itemTypeId);
  return (
    <button
      type="button"
      onClick={() => openItemPanel({ itemId: row.id, panelTab: TabValue.DEPENDENCIES })}
      className="flex w-full min-w-0 cursor-pointer items-center gap-2 text-left hover:underline"
    >
      {!!itemType && <ItemTypeIcon itemType={itemType} />}
      <span className="flex-1 min-w-0 truncate font-medium leading-5 text-text-neutral-primary">
        {row.name}
      </span>
    </button>
  );
};

interface AddDependencyControlProps {
  addLabel: string;
  pickerFilter: ItemFiltersType;
  excludedItemIds: number[];
  itemTypesById: ItemTypeMap;
  isAdding: boolean;
  isSubmitting: boolean;
  errorMessage: string | null;
  onOpen: () => void;
  onCancel: () => void;
  onSelect: (pickedItemId: number) => void;
}

const AddDependencyControl: React.FC<AddDependencyControlProps> = ({
  addLabel,
  pickerFilter,
  excludedItemIds,
  itemTypesById,
  isAdding,
  isSubmitting,
  errorMessage,
  onOpen,
  onCancel,
  onSelect,
}) => {
  const { t } = useTranslation();

  if (!isAdding) {
    return (
      <Button variant="secondary" size="sm" onClick={onOpen} className="shrink-0 gap-1">
        <Add />
        {addLabel}
      </Button>
    );
  }

  return (
    <fieldset
      aria-label={addLabel}
      className="flex w-full flex-col gap-1 border-0 p-0 px-1 py-2"
      onKeyDown={(event) => {
        if (event.key === 'Escape' && !isSubmitting) {
          event.stopPropagation();
          onCancel();
        }
      }}
    >
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <ItemPicker
            value={null}
            onChange={(picked) => {
              if (picked) {
                onSelect(picked.id);
              }
            }}
            itemTypesById={itemTypesById}
            filter={pickerFilter}
            excludedItemIds={excludedItemIds}
            placeholder={t(
              ($) => $.adaptivePlanner.itemPanel.dependenciesSection.pickerPlaceholder,
            )}
            autoFocus
            disabled={isSubmitting}
            isClearable={false}
          />
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={onCancel}
          disabled={isSubmitting}
          aria-label={t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.cancelAdd)}
          className="shrink-0"
        >
          <Close />
        </Button>
      </div>
      {!!errorMessage && (
        <span
          role="alert"
          aria-live="polite"
          className="text-xs text-text-error"
          data-testid="add-dependency-error"
        >
          {errorMessage}
        </span>
      )}
    </fieldset>
  );
};

interface DependencySectionProps {
  value: DependencySectionValue;
  title: string;
  description?: string;
  blockedCount: number;
  columns: Column[];
  rows: ItemWithSchedule[];
  rowClassName?: (row: ItemWithSchedule) => string | undefined;
  addControl?: React.ReactNode;
  removeError?: string | null;
}

const DependencySection: React.FC<DependencySectionProps> = ({
  value,
  title,
  description,
  blockedCount,
  columns,
  rows,
  rowClassName,
  addControl,
  removeError,
}) => {
  const { t } = useTranslation();
  return (
    <AccordionItem
      value={value}
      className="rounded-lg border border-border-neutral bg-bg-surface-base px-4 last:border"
    >
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <AccordionTrigger className="w-full text-sm font-semibold leading-5 text-text-neutral-primary hover:no-underline">
            <span className="flex flex-1 items-start gap-2">
              <span className="flex-1">
                {title} ({rows.length})
                {!!description && (
                  <span className="mt-1 block text-sm font-normal leading-5 text-text-neutral-secondary">
                    {description}
                  </span>
                )}
              </span>
              <SystemStatusBadge variant="error">
                {t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.blocked, {
                  count: blockedCount,
                })}
              </SystemStatusBadge>
            </span>
          </AccordionTrigger>
        </div>
        {addControl}
      </div>
      <AccordionContent className="pt-2">
        <ScrollableTable columns={columns} rows={rows} rowClassName={rowClassName} />
        {!!removeError && (
          <span
            role="alert"
            aria-live="polite"
            className="mt-2 block text-xs text-text-error"
            data-testid="remove-dependency-error"
          >
            {removeError}
          </span>
        )}
      </AccordionContent>
    </AccordionItem>
  );
};

interface DependenciesProps {
  item: Item;
}

export const useBlockedItemIds = (itemIds: number[]): Set<number> =>
  useQueries({
    queries: itemIds.map(
      (id): UseQueryOptions<ItemHealth | undefined> => ({
        queryKey: insightsQueryKeys.health.byItemId(id),
        queryFn: async () => {
          const response = await HealthApi.getItemHealth({ itemIds: [id] });
          return response.data[0];
        },
        staleTime: INSIGHTS_STALE_TIME,
      }),
    ),
    combine: (results) =>
      new Set(itemIds.filter((_, itemIndex) => results[itemIndex]?.data?.isBlocked ?? false)),
  });

const blockedRowClassName = (blockedIds: Set<number>) => (row: Item) =>
  blockedIds.has(row.id) ? 'bg-bg-error-subtler hover:bg-bg-error-subtler-hovered' : undefined;

const DEFAULT_OPEN_SECTIONS: DependencySectionValue[] = ['predecessors', 'successors'];

export const Dependencies = ({ item }: DependenciesProps) => {
  const { t } = useTranslation();
  const adapter = useTabAdapter<DependencyTabAdapter>();
  const { itemStatuses, itemTypes, predecessors, successors } = useItemDependenciesPanelQuery(item);
  const predecessorIds = useMemo(() => predecessors.map((p) => p.id), [predecessors]);
  const successorIds = useMemo(() => successors.map((s) => s.id), [successors]);
  const allDependencyIds = useMemo(
    () => [...predecessorIds, ...successorIds],
    [predecessorIds, successorIds],
  );
  const { data: schedules } = useGetSchedulesByItemIds(allDependencyIds);
  const blockedIds = useBlockedItemIds(allDependencyIds);

  const itemTypesById = useMemo<ItemTypeMap>(() => associateById(itemTypes), [itemTypes]);
  const excludedItemIds = useMemo(
    () => [item.id, ...predecessorIds, ...successorIds],
    [item.id, predecessorIds, successorIds],
  );
  const canAdd = adapter.canAddDependency() && adapter.loopId != null;
  const canRemove = adapter.isEditable();
  const pickerFilter = useMemo<ItemFiltersType>(
    () => (adapter.loopId != null ? { loopId: { value: [adapter.loopId], operator: 'eq' } } : {}),
    [adapter.loopId],
  );

  const [flow, setFlow] = useState<AddFlow>({ kind: 'idle' });
  const [removeFlow, setRemoveFlow] = useState<RemoveFlow>({ kind: 'idle' });

  const dependencyValidationMessage = (error: unknown): string | null => {
    if (error instanceof SelfDependencyError) {
      return t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.selfDependencyError);
    }

    if (error instanceof InvalidDependencyPairError) {
      return t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.invalidDependencyPairError);
    }

    if (error instanceof ReadOnlyAdapterError) {
      return t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.readOnlyError);
    }

    return null;
  };

  const submit = async (section: DependencySectionValue, pickedItemId: number): Promise<void> => {
    const [predecessorId, successorId] =
      section === 'predecessors' ? [pickedItemId, item.id] : [item.id, pickedItemId];
    setFlow({ kind: 'submitting', section });
    try {
      await adapter.addDependency(predecessorId, successorId);
      setFlow({ kind: 'idle' });
    } catch (error) {
      const message =
        dependencyValidationMessage(error) ??
        (await extractServerMessage(error)) ??
        t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.addError);
      setFlow({ kind: 'error', section, message });
    }
  };

  const remove = async (section: DependencySectionValue, rowItemId: number): Promise<void> => {
    if (removeFlow.kind === 'submitting') return;
    const [predecessorId, successorId] =
      section === 'predecessors' ? [rowItemId, item.id] : [item.id, rowItemId];
    setRemoveFlow({ kind: 'submitting', section, itemId: rowItemId });
    try {
      await adapter.removeDependency(predecessorId, successorId);
      setRemoveFlow({ kind: 'idle' });
    } catch (error) {
      const message =
        dependencyValidationMessage(error) ??
        (await extractServerMessage(error)) ??
        t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.removeError);
      setRemoveFlow({ kind: 'error', section, itemId: rowItemId, message });
    }
  };

  const withSchedules = (items: Item[]): ItemWithSchedule[] =>
    items.map((dep) => ({
      ...dep,
      expectedStartDateTime: schedules?.[dep.id]?.expectedStartDateTime ?? null,
      expectedFinishDateTime: schedules?.[dep.id]?.expectedFinishDateTime ?? null,
    }));

  const isRemoveInFlight = removeFlow.kind === 'submitting';

  const buildColumns = (nameHeader: string, section: DependencySectionValue): Column[] => {
    const columns: Column[] = [
      {
        id: 'name',
        header: nameHeader,
        cell: (row) => <DependencyNameCell row={row} itemTypes={itemTypes} />,
      },
      {
        id: 'status',
        header: t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.status),
        cell: (row) => <StatusCell row={row} itemStatuses={itemStatuses} />,
      },
      section === 'successors'
        ? {
            id: 'expectedStart',
            header: t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.expectedStart),
            cell: (row) =>
              row.expectedStartDateTime
                ? formatLocalDate(row.expectedStartDateTime, 'd MMMM yy')
                : '-',
          }
        : {
            id: 'expectedFinish',
            header: t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.expectedFinish),
            cell: (row) =>
              row.expectedFinishDateTime
                ? formatLocalDate(row.expectedFinishDateTime, 'd MMMM yy')
                : '-',
          },
    ];
    if (canRemove) {
      columns.push({
        id: 'actions',
        header: '',
        cell: (row) => (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              void remove(section, row.id);
            }}
            disabled={isRemoveInFlight}
            aria-label={t(
              ($) => $.adaptivePlanner.itemPanel.dependenciesSection.removeDependencyAriaLabel,
              { itemName: row.name },
            )}
            className="shrink-0"
          >
            <Delete />
          </Button>
        ),
      });
    }
    return columns;
  };

  const sections = [
    {
      value: 'predecessors' as const,
      title: t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.predecessors),
      description: t(
        ($) => $.adaptivePlanner.itemPanel.dependenciesSection.predecessorsDescription,
      ),
      addLabel: t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.addPredecessor),
      nameHeader: t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.waitingOn),
      items: predecessors,
    },
    {
      value: 'successors' as const,
      title: t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.successors),
      description: t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.successorsDescription),
      addLabel: t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.addSuccessor),
      nameHeader: t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.completionEnables),
      items: successors,
    },
  ];

  const renderAddControl = (section: DependencySectionValue, addLabel: string): React.ReactNode => {
    if (!canAdd) {
      return null;
    }
    const isAdding = flow.kind !== 'idle' && flow.section === section;
    return (
      <AddDependencyControl
        addLabel={addLabel}
        pickerFilter={pickerFilter}
        excludedItemIds={excludedItemIds}
        itemTypesById={itemTypesById}
        isAdding={isAdding}
        isSubmitting={isAdding && flow.kind === 'submitting'}
        errorMessage={isAdding && flow.kind === 'error' ? flow.message : null}
        onOpen={() => setFlow({ kind: 'open', section })}
        onCancel={() => setFlow({ kind: 'idle' })}
        onSelect={(pickedItemId) => {
          void submit(section, pickedItemId);
        }}
      />
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-row items-center gap-1 text-lg font-bold text-text-neutral-secondary">
        <Dependency /> {t(($) => $.adaptivePlanner.itemPanel.dependencies)}
      </div>
      <Accordion
        type="multiple"
        defaultValue={DEFAULT_OPEN_SECTIONS}
        className="flex flex-col gap-4"
      >
        {sections.map((section) => {
          const dateField =
            section.value === 'successors' ? 'expectedStartDateTime' : 'expectedFinishDateTime';
          const rows = withSchedules(section.items).sort((a, b) => {
            const aDate = a[dateField];
            const bDate = b[dateField];
            if (aDate === null && bDate === null) return 0;
            if (aDate === null) return 1;
            if (bDate === null) return -1;
            return aDate < bDate ? -1 : aDate > bDate ? 1 : 0;
          });
          return (
            <DependencySection
              key={section.value}
              value={section.value}
              title={section.title}
              description={section.description}
              blockedCount={rows.filter((row) => blockedIds.has(row.id)).length}
              columns={buildColumns(section.nameHeader, section.value)}
              rows={rows}
              rowClassName={blockedRowClassName(blockedIds)}
              addControl={renderAddControl(section.value, section.addLabel)}
              removeError={
                removeFlow.kind === 'error' && removeFlow.section === section.value
                  ? removeFlow.message
                  : null
              }
            />
          );
        })}
      </Accordion>
    </div>
  );
};
